import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, AlertTriangle, CheckCircle2, RotateCcw, ArrowRightLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { adminMigrationApi, type PreparePlan, type MigrationState } from "@/api/admin-migration";

interface Props {
  agentId: string;
  /** Current runtime; we only allow switching to the OTHER one */
  currentAdapter: string;
  /**
   * Subagents migrate INDIVIDUALLY (side-by-side): only ProClaw Runtime is a
   * valid target, and the parent + sibling subagents stay on their runtime.
   */
  isSubagent?: boolean;
  onClose: () => void;
}

const ADAPTER_LABEL: Record<string, string> = {
  openclaw_gateway: "OpenClaw gateway",
  anthropic_native: "Anthropic Native SDK",
  proclaw_runtime: "ProClaw Runtime",
};

// Valid migration targets per current runtime. ProClaw Runtime is listed first
// (the recommended target — owned, model-agnostic, broker-authed).
const TARGETS_OF: Record<string, string[]> = {
  openclaw_gateway: ["proclaw_runtime", "anthropic_native"],
  anthropic_native: ["proclaw_runtime", "openclaw_gateway"],
  proclaw_runtime: ["openclaw_gateway", "anthropic_native"],
};

const ACTIVE_PHASES = new Set([
  "preparing",
  "swapping",
  "verifying",
  "rolling_back",
]);

/**
 * Modal that walks an instance-admin through migrating an agent's runtime
 * in place on its existing VM. Two distinct stages:
 *
 *   1. PLAN — call /prepare to snapshot state + receive warnings.
 *      Render the warnings + a confirm button.
 *   2. EXECUTE — fire /execute (long-running) and poll /migrate for
 *      live phase transitions. When done or failed, show terminal UI.
 *
 * Polling is every 3s, only while a migration is in flight. After
 * terminal state (done/failed) the polling stops and a Close /
 * Rollback / Clear button is shown depending on outcome.
 */
export function AgentMigrateRuntimeModal({ agentId, currentAdapter, isSubagent, onClose }: Props) {
  const qc = useQueryClient();
  const [stage, setStage] = useState<"plan" | "running" | "terminal">("plan");
  const [plan, setPlan] = useState<PreparePlan | null>(null);
  const [planError, setPlanError] = useState<string | null>(null);
  // Subagents move ONE at a time, side-by-side, and only to ProClaw Runtime —
  // the server's prepareSubagentSwap enforces the same constraint.
  const targets = isSubagent ? ["proclaw_runtime"] : (TARGETS_OF[currentAdapter] ?? []);
  const [target, setTarget] = useState<string>(targets[0] ?? "");

  // Live state polling — only fetches while we're in the running stage
  // so an idle modal doesn't hammer the API.
  const stateQuery = useQuery({
    queryKey: ["admin-migration", agentId],
    queryFn: () => adminMigrationApi.get(agentId),
    enabled: stage === "running",
    refetchInterval: 3000,
  });

  const state: MigrationState | null = stateQuery.data?.state ?? null;
  // When polling sees a terminal state, flip stage so the modal stops
  // refetching and renders the outcome card.
  if (state && stage === "running" && !ACTIVE_PHASES.has(state.phase)) {
    setStage("terminal");
  }

  const prepareMut = useMutation({
    mutationFn: () => adminMigrationApi.prepare(agentId, target),
    onSuccess: (data) => {
      setPlan(data);
      setPlanError(null);
    },
    onError: (err) => setPlanError(err instanceof Error ? err.message : String(err)),
  });

  const executeMut = useMutation({
    mutationFn: () => adminMigrationApi.execute(agentId),
    onMutate: () => setStage("running"),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["admin-migration", agentId] });
      qc.invalidateQueries({ queryKey: ["agents"] });
    },
  });

  const rollbackMut = useMutation({
    mutationFn: () => adminMigrationApi.rollback(agentId, "manual admin rollback"),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-migration", agentId] });
      qc.invalidateQueries({ queryKey: ["agents"] });
      onClose();
    },
  });

  const clearMut = useMutation({
    mutationFn: () => adminMigrationApi.clear(agentId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-migration", agentId] });
      onClose();
    },
  });

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="w-[min(560px,90vw)] rounded-xl border border-border bg-background p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center gap-2">
          <ArrowRightLeft size={16} className="text-accent" />
          <h2 className="text-[15px] font-semibold">Migrate agent runtime</h2>
        </div>

        {stage === "plan" && (
          <div className="space-y-4">
            <div className="rounded-lg border border-border bg-surface p-3">
              <p className="text-[12.5px] text-text-secondary">
                Move this agent's runtime from{" "}
                <span className="font-medium">{ADAPTER_LABEL[currentAdapter] ?? currentAdapter}</span>{" "}
                to{" "}
                <span className="font-medium text-accent">{ADAPTER_LABEL[target] ?? target}</span>{" "}
                on the same VM. The agent UUID, API keys, private IP, and chat history stay
                unchanged.
              </p>
            </div>

            {!plan && targets.length > 1 && (
              <div className="space-y-1.5">
                <p className="text-[12px] font-medium text-text-secondary">Target runtime</p>
                <div className="grid grid-cols-1 gap-1.5">
                  {targets.map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setTarget(t)}
                      className={`rounded-lg border px-3 py-2 text-left text-[12.5px] transition ${
                        target === t
                          ? "border-accent bg-accent/5 font-medium text-accent"
                          : "border-border bg-surface text-text-secondary hover:border-accent/40"
                      }`}
                    >
                      {ADAPTER_LABEL[t] ?? t}
                      {t === "proclaw_runtime" && (
                        <span className="ml-2 text-[10.5px] text-text-muted">recommended</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {!plan && (
              <Button
                onClick={() => prepareMut.mutate()}
                disabled={prepareMut.isPending || !target}
                className="w-full"
              >
                {prepareMut.isPending ? (
                  <>
                    <Loader2 size={14} className="mr-2 animate-spin" />
                    Preparing…
                  </>
                ) : (
                  "Plan the migration"
                )}
              </Button>
            )}

            {planError && (
              <div className="rounded-lg border border-red-500/40 bg-red-500/5 p-3 text-[12.5px] text-red-600">
                <AlertTriangle size={12} className="mr-1 inline" />
                {planError}
              </div>
            )}

            {plan && (
              <>
                <div className="space-y-2 rounded-lg border border-yellow-500/40 bg-yellow-500/5 p-3">
                  <p className="text-[12px] font-semibold uppercase tracking-wide text-yellow-700">
                    Heads up
                  </p>
                  <ul className="list-disc space-y-1 pl-4 text-[12.5px] text-text-secondary">
                    {plan.warnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                </div>

                <div className="flex gap-2">
                  <Button variant="outline" onClick={onClose} className="flex-1">
                    Cancel
                  </Button>
                  <Button
                    onClick={() => executeMut.mutate()}
                    disabled={executeMut.isPending}
                    className="flex-1"
                  >
                    {executeMut.isPending ? (
                      <>
                        <Loader2 size={14} className="mr-2 animate-spin" />
                        Starting…
                      </>
                    ) : (
                      "Confirm migrate"
                    )}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        {stage === "running" && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 rounded-lg border border-accent/30 bg-accent/5 p-3">
              <Loader2 size={14} className="animate-spin text-accent" />
              <div className="flex-1">
                <p className="text-[12.5px] font-medium">
                  {state?.phase === "preparing" && "Snapshotting current runtime…"}
                  {state?.phase === "swapping" && "Stopping old runtime, installing new one on VM…"}
                  {state?.phase === "verifying" && "Waiting for new runtime to come online…"}
                  {state?.phase === "rolling_back" && "Migration failed — restoring previous runtime…"}
                  {!state?.phase && "Starting…"}
                </p>
                <p className="mt-0.5 text-[11px] text-text-muted">
                  Chat to this agent is paused until done (typically 2-3 min).
                </p>
              </div>
            </div>
          </div>
        )}

        {stage === "terminal" && state && (
          <div className="space-y-3">
            {state.phase === "done" && (
              <div className="flex items-start gap-2 rounded-lg border border-green-500/40 bg-green-500/5 p-3">
                <CheckCircle2 size={14} className="mt-0.5 text-green-600" />
                <div className="flex-1">
                  <p className="text-[12.5px] font-medium text-green-700">Migration complete</p>
                  <p className="mt-1 text-[12px] text-text-secondary">
                    Now running {ADAPTER_LABEL[state.toAdapter] ?? state.toAdapter}. Chat is available
                    again.
                  </p>
                </div>
              </div>
            )}
            {state.phase === "failed" && (
              <div className="flex items-start gap-2 rounded-lg border border-red-500/40 bg-red-500/5 p-3">
                <AlertTriangle size={14} className="mt-0.5 text-red-600" />
                <div className="flex-1">
                  <p className="text-[12.5px] font-medium text-red-700">Migration failed</p>
                  <p className="mt-1 text-[12px] text-text-secondary">
                    {state.error ?? "Unknown error"}
                  </p>
                  <p className="mt-1 text-[11px] text-text-muted">
                    The rollback attempt also failed — the agent may be in an inconsistent state.
                    Try a manual rollback below or inspect the VM via SSH.
                  </p>
                </div>
              </div>
            )}
            {state.log && (
              <details className="rounded-lg border border-border bg-surface p-2">
                <summary className="cursor-pointer text-[12px] text-text-secondary">
                  Show install log ({state.log.split("\n").length} lines)
                </summary>
                <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-all text-[11px] text-text-secondary">
                  {state.log}
                </pre>
              </details>
            )}
            <div className="flex gap-2">
              {state.phase === "failed" && (
                <Button
                  variant="outline"
                  onClick={() => rollbackMut.mutate()}
                  disabled={rollbackMut.isPending}
                  className="flex-1"
                >
                  <RotateCcw size={12} className="mr-1.5" />
                  Manual rollback
                </Button>
              )}
              <Button
                onClick={() => clearMut.mutate()}
                disabled={clearMut.isPending}
                className="flex-1"
              >
                {clearMut.isPending ? <Loader2 size={14} className="mr-2 animate-spin" /> : null}
                Close
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
